import { laboratories } from "./laboratories/path";

import { generatePath } from "@routes/utils";
import type { IExtractParams } from "@routes/types";
import { paperApprove } from "@api/members/papers/approve/path";
import { paperDisapprove } from "@api/members/papers/disapprove/path";
import { course } from "@api/members/course/path";
import { exportExcel } from "@api/members/exportExcel/path";

const pathBase = `/members/`;
const pathById = `${pathBase}:memberId/`;
const academicDegreePath = `${pathById}academic-degrees/`;
const academicDegreePathById = `${academicDegreePath}:academicDegreeId/`;
const achievementPath = `${pathById}achievements/`;
const achievementPathById = `${achievementPath}:achievementId/`;
const bookPath = `${pathById}books/`;
const bookPathById = `${bookPath}:bookId/`;
const workingExperiencePath = `${pathById}working-experience/`;
const workingExperiencePathById = `${pathBase}working-experience/:experienceId/`;
const googleScholarPath = `${pathById}google-scholar-data/`;
const googleScholarCandidatePath = `${googleScholarPath}candidates/`;
const scopusPath = `${pathById}scopus-data/`;
const scopusCandidatePath = `${scopusPath}candidates/`;
const researchProjectPath = `${pathById}research-projects/`;
const researchProjectPathById = `${researchProjectPath}:projectId/`;
const studentPathById = `${pathBase}students/:studentId/`;
const memberStudents = `${pathById}students`;
const mediaPath = `${pathById}media/`;
const mediaPathById = `${mediaPath}:mediaId/`;
const patentPath = `${pathById}patents/`;
const patentPathById = `${patentPath}:patentId/`;
const thesesPath = `${pathById}theses/`;
const thesesPathById = `${thesesPath}:thesesId/`;
const researchFieldPath = `${pathById}academic-domain/`;
const researchFieldPathById = `${researchFieldPath}:researchFieldId/`;
const paperPath = `${pathById}papers/`;
const paperPathById = `${paperPath}:paperId/`;
const socialMediaPath = `/users/:memberId/social-media-links/`;
const socialMediaPathById = `${socialMediaPath}:socialMediaId/`;

export type IMemberPathParam = IExtractParams<typeof pathById>;
export type IMemberAcademicDegreePathParam = IExtractParams<
  typeof academicDegreePathById
>;
export type IMemberAchievementsPathParam = IExtractParams<
  typeof achievementPathById
>;
export type IMemberBooksPathParam = IExtractParams<typeof bookPathById>;
export type IMemberStudentsPathParam = IExtractParams<typeof studentPathById>;
export type IMemberWorkingExperiencePathParam = IExtractParams<
  typeof workingExperiencePathById
>;
export type IMemberResearchProjectPathParam = IExtractParams<
  typeof researchProjectPathById
>;
export type IMemberPapersPathParam = IExtractParams<typeof paperPathById>;
export type IMemberMediaPathParam = IExtractParams<typeof mediaPathById>;

export type IMemberPatentPathParams = IExtractParams<typeof patentPathById>;
export type IMemberThesesPathParams = IExtractParams<typeof thesesPathById>;
export type IMemberResearchFieldPathParams = IExtractParams<
  typeof researchFieldPathById
>;

export type IMemberSocialMediaPathParam = IExtractParams<
  typeof socialMediaPathById
>;

export const members = {
  getAll: () => generatePath(pathBase, null),
  post: () => generatePath(pathBase, null),
  get: (param: IMemberPathParam) => generatePath(pathById, param),
  put: (param: IMemberPathParam) => generatePath(pathById, param),
  patch: (param: IMemberPathParam) => generatePath(pathById, param),
  delete: (param: IMemberPathParam) => generatePath(pathById, param),
  patents: {
    getAll: (param: IMemberPathParam) => generatePath(patentPath, param),
    post: (param: IMemberPathParam) => generatePath(patentPath, param),
    get: (param: IMemberPatentPathParams) =>
      generatePath(patentPathById, param),
    put: (param: IMemberPatentPathParams) =>
      generatePath(patentPathById, param),
    patch: (param: IMemberPatentPathParams) =>
      generatePath(patentPathById, param),
    delete: (param: IMemberPatentPathParams) =>
      generatePath(patentPathById, param),
  },
  researchField: {
    getAll: (param: IMemberPathParam) => generatePath(researchFieldPath, param),
    post: (param: IMemberPathParam) => generatePath(researchFieldPath, param),
    get: (param: IMemberResearchFieldPathParams) =>
      generatePath(researchFieldPathById, param),
    put: (param: IMemberResearchFieldPathParams) =>
      generatePath(researchFieldPathById, param),
    patch: (param: IMemberResearchFieldPathParams) =>
      generatePath(researchFieldPathById, param),
    delete: (param: IMemberResearchFieldPathParams) =>
      generatePath(researchFieldPathById, param),
  },
  socialMedia: {
    getAll: (param: IMemberPathParam) => generatePath(socialMediaPath, param),
    post: (param: IMemberPathParam) => generatePath(socialMediaPath, param),
    get: (param: IMemberSocialMediaPathParam) =>
      generatePath(socialMediaPathById, param),
    put: (param: IMemberSocialMediaPathParam) =>
      generatePath(socialMediaPathById, param),
    patch: (param: IMemberSocialMediaPathParam) =>
      generatePath(socialMediaPathById, param),
    delete: (param: IMemberSocialMediaPathParam) =>
      generatePath(socialMediaPathById, param),
  },
  theses: {
    getAll: (param: IMemberPathParam) => generatePath(thesesPath, param),
    post: (param: IMemberPathParam) => generatePath(thesesPath, param),
    get: (param: IMemberThesesPathParams) =>
      generatePath(thesesPathById, param),
    put: (param: IMemberThesesPathParams) =>
      generatePath(thesesPathById, param),
    patch: (param: IMemberThesesPathParams) =>
      generatePath(thesesPathById, param),
    delete: (param: IMemberThesesPathParams) =>
      generatePath(thesesPathById, param),
  },
  academicDegree: {
    getAll: (param: IMemberPathParam) =>
      generatePath(academicDegreePath, param),
    post: (param: IMemberPathParam) => generatePath(academicDegreePath, param),
    get: (param: IMemberAcademicDegreePathParam) =>
      generatePath(academicDegreePathById, param),
    put: (param: IMemberAcademicDegreePathParam) =>
      generatePath(academicDegreePathById, param),
    patch: (param: IMemberAcademicDegreePathParam) =>
      generatePath(academicDegreePathById, param),
    delete: (param: IMemberAcademicDegreePathParam) =>
      generatePath(academicDegreePathById, param),
  },
  achievements: {
    getAll: (param: IMemberPathParam) => generatePath(achievementPath, param),
    post: (param: IMemberPathParam) => generatePath(achievementPath, param),
    get: (param: IMemberAchievementsPathParam) =>
      generatePath(achievementPathById, param),
    put: (param: IMemberAchievementsPathParam) =>
      generatePath(achievementPathById, param),
    patch: (param: IMemberAchievementsPathParam) =>
      generatePath(achievementPathById, param),
    delete: (param: IMemberAchievementsPathParam) =>
      generatePath(achievementPathById, param),
  },
  books: {
    getAll: (param: IMemberPathParam) => generatePath(bookPath, param),
    post: (param: IMemberPathParam) => generatePath(bookPath, param),
    get: (param: IMemberBooksPathParam) => generatePath(bookPathById, param),
    put: (param: IMemberBooksPathParam) => generatePath(bookPathById, param),
    patch: (param: IMemberBooksPathParam) => generatePath(bookPathById, param),
    delete: (param: IMemberBooksPathParam) => generatePath(bookPathById, param),
  },
  researchProject: {
    getAll: (param: IMemberPathParam) =>
      generatePath(researchProjectPath, param),
    post: (param: IMemberPathParam) => generatePath(researchProjectPath, param),
    get: (param: IMemberResearchProjectPathParam) =>
      generatePath(researchProjectPathById, param),
    put: (param: IMemberResearchProjectPathParam) =>
      generatePath(researchProjectPathById, param),
    patch: (param: IMemberResearchProjectPathParam) =>
      generatePath(researchProjectPathById, param),
    delete: (param: IMemberResearchProjectPathParam) =>
      generatePath(researchProjectPathById, param),
  },
  workingExperience: {
    getAll: (param: IMemberPathParam) =>
      generatePath(workingExperiencePath, param),
    post: (param: IMemberPathParam) =>
      generatePath(workingExperiencePath, param),
    get: (param: IMemberWorkingExperiencePathParam) =>
      generatePath(workingExperiencePathById, param),
    put: (param: IMemberWorkingExperiencePathParam) =>
      generatePath(workingExperiencePathById, param),
    patch: (param: IMemberWorkingExperiencePathParam) =>
      generatePath(workingExperiencePathById, param),
    delete: (param: IMemberWorkingExperiencePathParam) =>
      generatePath(workingExperiencePathById, param),
  },
  googleScholar: {
    get: (param: IMemberPathParam) => generatePath(googleScholarPath, param),
    put: (param: IMemberPathParam) => generatePath(googleScholarPath, param),
    patch: (param: IMemberPathParam) => generatePath(googleScholarPath, param),
    delete: (param: IMemberPathParam) => generatePath(googleScholarPath, param),
    candidate: {
      get: (param: IMemberPathParam) =>
        generatePath(googleScholarCandidatePath, param),
      post: (param: IMemberPathParam) =>
        generatePath(googleScholarCandidatePath, param),
    },
  },
  scopus: {
    get: (param: IMemberPathParam) => generatePath(scopusPath, param),
    put: (param: IMemberPathParam) => generatePath(scopusPath, param),
    patch: (param: IMemberPathParam) => generatePath(scopusPath, param),
    delete: (param: IMemberPathParam) => generatePath(scopusPath, param),
    candidate: {
      get: (param: IMemberPathParam) =>
        generatePath(scopusCandidatePath, param),
      post: (param: IMemberPathParam) =>
        generatePath(scopusCandidatePath, param),
    },
  },
  students: {
    getAll: (param: IMemberPathParam) => generatePath(memberStudents, param),
    post: (param: IMemberPathParam) => generatePath(memberStudents, param),
    get: (param: IMemberStudentsPathParam) =>
      generatePath(studentPathById, param),
    put: (param: IMemberStudentsPathParam) =>
      generatePath(studentPathById, param),
    patch: (param: IMemberStudentsPathParam) =>
      generatePath(studentPathById, param),
    delete: (param: IMemberStudentsPathParam) =>
      generatePath(studentPathById, param),
  },
  papers: {
    getAll: (param: IMemberPathParam) => generatePath(paperPath, param),
    post: (param: IMemberPathParam) => generatePath(paperPath, param),
    get: (param: IMemberPapersPathParam) => generatePath(paperPathById, param),
    put: (param: IMemberPapersPathParam) => generatePath(paperPathById, param),
    patch: (param: IMemberPapersPathParam) =>
      generatePath(paperPathById, param),
    delete: (param: IMemberPapersPathParam) =>
      generatePath(paperPathById, param),
    paperApprove,
    paperDisapprove,
  },
  media: {
    getAll: (param: IMemberPathParam) => generatePath(mediaPath, param),
    post: (param: IMemberPathParam) => generatePath(mediaPath, param),
    get: (param: IMemberMediaPathParam) => generatePath(mediaPathById, param),
    put: (param: IMemberMediaPathParam) => generatePath(mediaPathById, param),
    patch: (param: IMemberMediaPathParam) => generatePath(mediaPathById, param),
    delete: (param: IMemberMediaPathParam) =>
      generatePath(mediaPathById, param),
  },
  laboratories,
  course,
  exportExcel,
};
